/**
 * Single source of truth for MediaRecorder MIME-type selection.
 *
 * Previously two ladders had drifted apart:
 *  - `getMimeType()` in hooks/useAudioPipeline.ts  → opus, webm, mp4, aac (no API guard)
 *  - `pickMimeType()` in hooks/useRemoteChannelStreaming.ts → opus, webm, mp4 (with API guard)
 *
 * This is the UNION of both, ordered most-specific → least-specific. Order matters:
 * MediaRecorder.isTypeSupported is probed in sequence and the first hit wins.
 */

/**
 * Default recorder MIME used when a concrete recording's type is unknown (IDB
 * replay fallbacks, upload ref seed). Equals the top of the ladder below.
 */
export const DEFAULT_RECORDER_MIME = "audio/webm;codecs=opus"

/**
 * Merged codec ladder, most-specific first. Origin of each entry:
 *  1. audio/webm;codecs=opus — both getMimeType + pickMimeType
 *  2. audio/webm             — both getMimeType + pickMimeType
 *  3. audio/mp4              — both getMimeType + pickMimeType (Safari lands here)
 *  4. audio/aac              — getMimeType only (last-ditch; below mp4, never
 *                              changes a browser that already matched an earlier entry)
 */
const RECORDER_MIME_LADDER: readonly string[] = [
  DEFAULT_RECORDER_MIME, // audio/webm;codecs=opus
  "audio/webm",
  "audio/mp4",
  "audio/aac",
]

/**
 * Walk the merged ladder and return the first MIME the platform's MediaRecorder
 * supports, or `null` when the API is unavailable or nothing matches — the same
 * final-fallback behaviour both original functions had (callers rely on the
 * nullable result: `getMimeType() ?? "audio/webm"`, `if (!mimeType) …`).
 */
export function pickRecorderMimeType(): string | null {
  if (typeof MediaRecorder === "undefined") return null
  for (const candidate of RECORDER_MIME_LADDER) {
    if (MediaRecorder.isTypeSupported(candidate)) return candidate
  }
  return null
}
